import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle } from 'lucide-react';
import { Button } from '@nepal-ecom/ui';

interface ReasonModalProps {
  open: boolean;
  title: string;
  description?: string;
  /** Preset reasons offered in the dropdown. The last option is always "Other". */
  presets: string[];
  confirmLabel: string;
  /** Visual emphasis — destructive uses red confirm button. */
  tone?: 'danger' | 'default';
  pending?: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

const OTHER = 'Other (write your own)';

/**
 * Reusable confirmation modal that collects a reason via a preset dropdown plus
 * an optional detail box. Replaces native prompt()/alert() so admins get a clean
 * UI and consistent, pre-written reasons they don't have to retype every time.
 */
export function ReasonModal({
  open,
  title,
  description,
  presets,
  confirmLabel,
  tone = 'danger',
  pending = false,
  onConfirm,
  onClose,
}: ReasonModalProps) {
  const [preset, setPreset] = useState(presets[0] ?? OTHER);
  const [detail, setDetail] = useState('');

  // Reset fields whenever the modal is (re)opened.
  useEffect(() => {
    if (open) {
      setPreset(presets[0] ?? OTHER);
      setDetail('');
    }
  }, [open, presets]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isOther = preset === OTHER;
  // Final reason = the preset label, optionally enriched with the detail note;
  // for "Other" the detail box IS the reason.
  const finalReason = isOther ? detail.trim() : detail.trim() ? `${preset} — ${detail.trim()}` : preset;
  const canConfirm = isOther ? detail.trim().length >= 5 : true;

  const handleConfirm = () => {
    if (!canConfirm || pending) return;
    onConfirm(finalReason);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-xl border border-slate-200 animate-fade-in">
        <div className="flex items-start gap-3 p-5 border-b border-slate-100">
          <div className="h-9 w-9 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-slate-900">{title}</h2>
            {description && <p className="text-sm text-slate-500 mt-0.5">{description}</p>}
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 -mt-1 -mr-1 p-1"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">Reason</label>
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              {presets.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
              <option value={OTHER}>{OTHER}</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              {isOther ? 'Write the reason' : 'Additional detail'}
              {!isOther && <span className="font-normal text-slate-400"> (optional)</span>}
            </label>
            <textarea
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              rows={3}
              placeholder={isOther ? 'Explain why…' : 'Anything else the vendor should know…'}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            {isOther && detail.trim().length > 0 && detail.trim().length < 5 && (
              <p className="text-xs text-red-600 mt-1">Please write at least 5 characters.</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-100">
          <Button variant="outline" onClick={onClose} disabled={pending}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm || pending}
            className={tone === 'danger' ? 'bg-red-600 hover:bg-red-700 text-white' : undefined}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
